/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { CluEntityDto } from 'src/clu/appShell/cluBuildSchemaPage/components/CluEntitiesList';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableColumnProps, TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { EntityCapability } from 'src/core/types/EntityStore';
import { routeTable } from 'src/core/utils/routeTable';

const messages = defineMessages({
    list: { id: 'clu.cluEntitiesTable.list', defaultMessage: 'List' },
    regex: { id: 'clu.cluEntitiesTable.regex', defaultMessage: 'Regex' },
    learned: { id: 'clu.cluEntitiesTable.learned', defaultMessage: 'Learned' },
    entities: { id: 'clu.cluEntitiesTable.entities', defaultMessage: 'Entities' },
    prebuilt: { id: 'clu.cluEntitiesTable.prebuilt', defaultMessage: 'Prebuilt' },
    usedInIntents: { id: 'clu.cluEntitiesTable.usedInIntents', defaultMessage: 'Used in intents' },
    entityComponents: { id: 'clu.cluEntitiesTable.entityComponents', defaultMessage: 'Entity components' },
    taggedInUtterances: { id: 'clu.cluEntitiesTable.taggedInUtterances', defaultMessage: 'Labeled in utterances' }
});

const { styleDiv } = getStylist('CluEntitiesTable');
const IntentsListWrapper = styleDiv('IntentsListWrapper', { display: 'flex', whiteSpace: 'break-spaces', flexWrap: 'wrap' });

export const CluEntitiesTable = observer((props: TableProps<CluEntityDto>) => {
    const { items, sortKey } = props;
    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const currentProjectName = rootStore.currentCluProjectStore.name;
    const routes = routeTable[rootStore.currentCluProjectStore.projectType === 'Conversation' ? 'clu' : 'orchestration'];

    const onRenderUsedInIntentsColumn = (item: CluEntityDto) => (
        <IntentsListWrapper>
            {item.intents.map((intent, index) => (
                <NameCellLink key={`${intent}-${index}`} onClick={() => onModelClick(intent, 'intent')}>
                    {index === item.intents.length - 1 ? intent : `${intent},`}
                    &nbsp;
                </NameCellLink>
            ))}
        </IntentsListWrapper>
    );

    const onRenderEntityDetailsColumn = (item: CluEntityDto) => {
        const getCapabilityText = (capability: EntityCapability) => {
            switch (capability) {
                case 'learned':
                    return localize(messages.learned);
                case 'prebuilt':
                    return localize(messages.prebuilt);
                case 'list':
                    return localize(messages.list);
                case 'regex':
                    return localize(messages.regex);
                default:
                    return '';
            }
        };

        return item.capabilities.map((capability, index) => (
            <NameCellLink
                key={`${item.entityName}-${capability}`}
                onClick={() => history.push(routes.entityDetailsPage(currentProjectName, item.entityName), { capability })}>
                {index === item.capabilities.length - 1 ? getCapabilityText(capability) : `${getCapabilityText(capability)},`}
                &nbsp;
            </NameCellLink>
        ));
    };

    const onModelClick = (modelName: string, modelType: 'intent' | 'entity') => {
        const state: { intentsIdsFilters: string[]; entitiesIdsFilters: string[] } = { intentsIdsFilters: [], entitiesIdsFilters: [] };

        modelType === 'intent' ? state.intentsIdsFilters.push(modelName) : state.entitiesIdsFilters.push(modelName);

        history.push(routes.dataTaggingPage(currentProjectName), state);
    };

    const columns: TableColumnProps[] = [
        {
            key: 'entityName',
            minWidth: 250,
            maxWidth: 300,
            isRowHeader: true,
            fieldName: 'name',
            isResizable: true,
            isSortable: true,
            isCollapsible: false,
            name: localize(messages.entities),
            isSorted: sortKey?.key === 'entityName',
            isSortedDescending: sortKey?.key === 'entityName' && sortKey?.order === 'desc',
            onRender: (item: CluEntityDto) => (
                <NameCellLink onClick={() => history.push(routes.entityDetailsPage(currentProjectName, item.entityName))}>
                    {item.entityName}
                </NameCellLink>
            )
        },
        {
            minWidth: 100,
            maxWidth: 140,
            isResizable: true,
            isRowHeader: false,
            key: 'labelsCount',
            isCollapsible: false,
            fieldName: 'labelsCount',
            isSorted: sortKey?.key === 'labelsCount',
            name: localize(messages.taggedInUtterances),
            isSortedDescending: sortKey?.key === 'labelsCount' && sortKey?.order === 'desc',
            onRender: (item: CluEntityDto) => (
                <Stack grow horizontalAlign="center">
                    <LuisText>{item.labelsCount}</LuisText>
                </Stack>
            )
        },
        {
            minWidth: 350,
            maxWidth: 550,
            key: 'intents',
            isResizable: true,
            isSortable: false,
            isRowHeader: false,
            isCollapsible: true,
            onRender: onRenderUsedInIntentsColumn,
            name: localize(messages.usedInIntents)
        },
        {
            minWidth: 350,
            maxWidth: 550,
            isResizable: true,
            isSortable: false,
            isRowHeader: false,
            isCollapsible: true,
            key: 'capabilities',
            name: localize(messages.entityComponents),
            onRender: onRenderEntityDetailsColumn
        }
    ];

    return <Table<CluEntityDto> {...props} columns={columns} items={items} />;
});
